import type * as React from "react";

import { cn } from "../lib/cn";

/**
 * What the blank stands for.
 *
 * `bar` is a line that has not been written yet: a recessed groove in the page,
 * the shape of the sentence that is coming.
 *
 * `sheet` is the piece of card stock the content arrives on - the same lifted
 * paper the real card will be, just blank. Reach for it whenever the placeholder
 * sits on a ground `bg-recess` cannot be seen against (manila folders, the
 * corkboard), and whenever what is loading is a whole card rather than a line.
 */
export type SkeletonShape = "bar" | "sheet";

const shapeSurface: Record<SkeletonShape, string> = {
  bar: "bg-recess",
  sheet: "border border-rule bg-card shadow-lift",
};

/**
 * Gap between one blank's pulse and the next one's, so a stack breathes in a
 * wave instead of flashing as a block. Small enough to read as one object.
 */
const STAGGER_MS = 80;

export interface SkeletonProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "children"
> {
  /** `bar` (a line in a well) or `sheet` (blank card stock). Default `bar`. */
  shape?: SkeletonShape;
  /** Place in a stack: each step delays the pulse by 80ms. */
  index?: number;
  /** The blank's size lives here - `h-16`, `w-3/4`, `rounded-full`. */
  className?: string;
  ref?: React.Ref<HTMLDivElement>;
}

/**
 * One blank waiting to be filled in. A skeleton is not a ghost of the content,
 * it is the paper the content arrives on, so it is drawn in the surfaces the
 * real thing will use and never in a grey borrowed from somewhere else.
 *
 * Size it with `className` (`h-16`, `w-3/4`); the pulse and its reduced-motion
 * stop are baked in. It is `aria-hidden`, because a placeholder has nothing to
 * announce - put `aria-busy` on the region that is loading.
 */
export function Skeleton({
  shape = "bar",
  index = 0,
  className,
  style,
  ...rest
}: SkeletonProps) {
  const delay = index * STAGGER_MS;

  return (
    <div
      aria-hidden="true"
      {...rest}
      className={cn(
        "animate-pulse rounded-paper motion-reduce:animate-none",
        shapeSurface[shape],
        className,
      )}
      // Caller's own style wins, so a one-off delay can still be set by hand.
      style={delay ? { animationDelay: `${delay}ms`, ...style } : style}
    />
  );
}

export interface SkeletonStackProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "children"
> {
  /** How many blanks. Enough to fill the panel, not enough to promise a count. */
  count: number;
  /** Passed to every blank. See `SkeletonShape`. */
  shape?: SkeletonShape;
  /** Each blank's size (`h-16`). The container's own `className` owns the gap. */
  itemClassName?: string;
  /** Defaults to `flex flex-col gap-2`; override the gap here. */
  className?: string;
  ref?: React.Ref<HTMLDivElement>;
}

/**
 * A short run of identical blanks - the shape a list takes before it has any
 * rows. Handles the repeat, the keys and the 80ms stagger that every hand-rolled
 * version of this was writing out.
 */
export function SkeletonStack({
  count,
  shape,
  itemClassName,
  className,
  ...rest
}: SkeletonStackProps) {
  return (
    <div
      aria-hidden="true"
      {...rest}
      className={cn("flex flex-col gap-2", className)}
    >
      {Array.from({ length: count }, (_, i) => (
        <Skeleton key={i} shape={shape} index={i} className={itemClassName} />
      ))}
    </div>
  );
}
